import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSettlementStore, UberGapInfo } from '../../stores/settlementStore';
import { useRentDiscountStore } from '../../stores/rentDiscountStore';
import { SettlementRecord, SettlementPayment, RentDiscount } from '../../types';
import PartialPaymentModal from '../../components/modals/PartialPaymentModal';
import theme from '../../theme';

const isWeb = Platform.OS === 'web';

interface SettlementDetailScreenProps {
  route: {
    params: {
      settlementId: string;
    };
  };
  navigation: any;
}

type TabKey = 'overview' | 'rent' | 'income' | 'expenses' | 'discounts' | 'payments' | 'documents';

export const SettlementDetailScreen: React.FC<any> = ({ route, navigation }) => {
  const { settlementId } = route.params;

  // Store hooks
  const {
    selectedSettlement,
    payments,
    loading,
    error,
    fetchSettlementById,
    getPaymentHistory,
    checkUberGaps,
    clearError,
  } = useSettlementStore();

  const {
    discounts,
    getDiscountsBySettlement,
  } = useRentDiscountStore();

  // Local state
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [uberGapInfo, setUberGapInfo] = useState<UberGapInfo | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadSettlementData();
  }, [settlementId]);

  const loadSettlementData = async () => {
    try {
      setRefreshing(true);

      // Load settlement details
      await fetchSettlementById(settlementId);

      // Load payment history
      await getPaymentHistory(settlementId);

      // Load discounts
      await getDiscountsBySettlement(settlementId);

      // Check Uber gaps
      const gapInfo = await checkUberGaps(settlementId);
      setUberGapInfo(gapInfo);

    } catch (err) {
      console.error('Error loading settlement data:', err);
      Alert.alert('Error', 'Failed to load settlement data');
    } finally {
      setRefreshing(false);
    }
  };

  const handlePaymentSuccess = () => {
    loadSettlementData();
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getMonthName = (month: number) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    return months[month - 1];
  };

  const getStatusColor = (status: SettlementRecord['status']) => {
    switch (status) {
      case 'pending': return theme.colors.warning;
      case 'approved': return theme.colors.info;
      case 'paid': return theme.colors.success;
      case 'disputed': return theme.colors.error;
      case 'cancelled': return theme.colors.textSecondary;
      default: return theme.colors.textSecondary;
    }
  };

  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'speedometer-outline' },
    { key: 'rent', label: 'Company Share Details', icon: 'cash-outline' },
    { key: 'income', label: 'Income', icon: 'trending-up-outline' },
    { key: 'expenses', label: 'Expenses', icon: 'trending-down-outline' },
    { key: 'discounts', label: 'Discounts', icon: 'pricetag-outline' },
    { key: 'payments', label: 'Payments', icon: 'wallet-outline' },
    { key: 'documents', label: 'Documents', icon: 'document-text-outline' },
  ] as const;

  if (loading && !selectedSettlement) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading settlement...</Text>
        </View>
      </View>
    );
  }

  if (!selectedSettlement) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={theme.colors.error} />
          <Text style={styles.emptyText}>Settlement not found</Text>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <Text style={styles.backButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const settlement = selectedSettlement;

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backIcon} onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <Text style={styles.headerTitle}>{settlement.driver?.name || 'Unknown Driver'}</Text>
          <Text style={styles.headerSubtitle}>
            {getMonthName(settlement.period_month)} {settlement.period_year} • {settlement.vehicle?.license_plate || 'N/A'}
          </Text>
        </View>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(settlement.status) }]}>
          <Text style={styles.statusText}>{settlement.status.toUpperCase()}</Text>
        </View>
      </View>

      {/* Uber Gap Warning Banner */}
      {uberGapInfo && !uberGapInfo.is_complete && (
        <View style={styles.warningBanner}>
          <Ionicons name="warning" size={20} color="#E67E22" />
          <View style={styles.warningContent}>
            <Text style={styles.warningTitle}>⚠️ Incomplete Uber Data</Text>
            <Text style={styles.warningText}>
              Missing {uberGapInfo.gap_days} days of Uber income ({uberGapInfo.actual_days}/{uberGapInfo.expected_days} days recorded)
            </Text>
          </View>
        </View>
      )}

      {/* Balance Summary Card */}
      <View style={styles.balanceCard}>
        <View style={styles.balanceRow}>
          <View style={styles.balanceItem}>
            <Text style={styles.balanceLabel}>Closing Balance</Text>
            <Text style={[styles.balanceValue, { color: (settlement.closing_balance ?? 0) >= 0 ? theme.colors.success : theme.colors.error }]}>
              {formatCurrency(settlement.closing_balance ?? 0)}
            </Text>
          </View>
          <View style={styles.balanceItem}>
            <Text style={styles.balanceLabel}>Outstanding</Text>
            <Text style={[styles.balanceValue, { color: (settlement.outstanding_balance ?? 0) > 0 ? theme.colors.error : theme.colors.success }]}>
              {formatCurrency(settlement.outstanding_balance ?? 0)}
            </Text>
          </View>
        </View>
        {(settlement.outstanding_balance ?? 0) > 0 && settlement.status === 'approved' && (
          <TouchableOpacity
            style={styles.recordPaymentButton}
            onPress={() => setShowPaymentModal(true)}
          >
            <Ionicons name="cash-outline" size={20} color="#FFFFFF" />
            <Text style={styles.recordPaymentText}>Record Payment</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Tabs */}
      <View style={styles.tabBar}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, activeTab === tab.key && styles.tabActive]}
              onPress={() => setActiveTab(tab.key as TabKey)}
            >
              <Ionicons
                name={tab.icon as any}
                size={18}
                color={activeTab === tab.key ? theme.colors.primary : theme.colors.textSecondary}
              />
              <Text style={[styles.tabText, activeTab === tab.key && styles.tabTextActive]}>
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tab Content */}
      <ScrollView style={styles.tabContent} showsVerticalScrollIndicator={false}>
        {activeTab === 'overview' && <OverviewTab settlement={settlement} uberGapInfo={uberGapInfo} />}
        {activeTab === 'rent' && <RentDetailsTab settlement={settlement} />}
        {activeTab === 'income' && <IncomeTab settlement={settlement} uberGapInfo={uberGapInfo} />}
        {activeTab === 'expenses' && <ExpensesTab settlement={settlement} />}
        {activeTab === 'discounts' && <DiscountsTab discounts={discounts} settlement={settlement} />}
        {activeTab === 'payments' && <PaymentsTab payments={payments} settlement={settlement} />}
        {activeTab === 'documents' && <DocumentsTab settlement={settlement} />}
      </ScrollView>

      {/* Partial Payment Modal */}
      {settlement && (
        <PartialPaymentModal
          isVisible={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          settlement={{
            id: settlement.id,
            driver_id: settlement.driver_id,
            driver_name: settlement.driver?.name || 'Unknown Driver',
            period_month: settlement.period_month,
            period_year: settlement.period_year,
            closing_balance: settlement.closing_balance ?? 0,
            outstanding_balance: settlement.outstanding_balance ?? 0,
          }}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      {/* Error Banner */}
      {error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={clearError}>
            <Ionicons name="close" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

// ===== TAB COMPONENTS =====

const OverviewTab: React.FC<{ settlement: SettlementRecord; uberGapInfo: UberGapInfo | null }> = ({ settlement, uberGapInfo }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(value);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <View style={styles.tabPanel}>
      {/* Period Info */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Settlement Period</Text>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Period:</Text>
          <Text style={styles.infoValue}>
            {formatDate(settlement.period_start_date)} - {formatDate(settlement.period_end_date)}
          </Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Total Days:</Text>
          <Text style={styles.infoValue}>{settlement.total_days || 0} days</Text>
        </View>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Assignment Type:</Text>
          <Text style={styles.infoValue}>{settlement.assignment?.is_shared ? 'Shared' : 'Exclusive'}</Text>
        </View>
      </View>

      {/* Financial Summary */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Financial Summary</Text>
        
        <View style={styles.summaryCard}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Opening Balance:</Text>
            <Text style={styles.summaryValue}>{formatCurrency(settlement.opening_balance || 0)}</Text>
          </View>
          
          <View style={[styles.summaryRow, styles.highlightRow]}>
            <Text style={styles.summaryLabel}>Company Share Charged:</Text>
            <Text style={[styles.summaryValue, { color: theme.colors.error }]}>
              {formatCurrency(settlement.total_rent_charged || 0)}
            </Text>
          </View>
          
          <View style={[styles.summaryRow, styles.highlightRow]}>
            <Text style={styles.summaryLabel}>Total Income:</Text>
            <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
              +{formatCurrency(settlement.total_income || 0)}
            </Text>
          </View>
          
          <View style={[styles.summaryRow, styles.highlightRow]}>
            <Text style={styles.summaryLabel}>Total Expenses:</Text>
            <Text style={[styles.summaryValue, { color: theme.colors.error }]}>
              -{formatCurrency((settlement.total_fines || 0) + (settlement.total_salik || 0) + (settlement.total_other_expenses || 0))}
            </Text>
          </View>
          
          {(settlement.rent_discount_amount ?? 0) > 0 && (
            <View style={[styles.summaryRow, styles.highlightRow]}>
              <Text style={styles.summaryLabel}>Share Adjustments:</Text>
              <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
                -{formatCurrency(settlement.rent_discount_amount ?? 0)}
              </Text>
            </View>
          )}
          
          <View style={styles.divider} />
          
          <View style={[styles.summaryRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Closing Balance:</Text>
            <Text style={[styles.totalValue, { color: (settlement.closing_balance ?? 0) >= 0 ? theme.colors.success : theme.colors.error }]}>
              {formatCurrency(settlement.closing_balance ?? 0)}
            </Text>
          </View>
          
          {(settlement.outstanding_balance ?? 0) !== (settlement.closing_balance ?? 0) && (
            <>
              <View style={[styles.summaryRow, styles.highlightRow]}>
                <Text style={styles.summaryLabel}>Payments Received:</Text>
                <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
                  -{formatCurrency((settlement.closing_balance ?? 0) - (settlement.outstanding_balance ?? 0))}
                </Text>
              </View>
              
              <View style={[styles.summaryRow, styles.totalRow]}>
                <Text style={styles.totalLabel}>Outstanding Balance:</Text>
                <Text style={[styles.totalValue, { color: (settlement.outstanding_balance ?? 0) > 0 ? theme.colors.error : theme.colors.success }]}>
                  {formatCurrency(settlement.outstanding_balance ?? 0)}
                </Text>
              </View>
            </>
          )}
        </View>
      </View>

      {/* Status & Dates */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Status Information</Text>
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Status:</Text>
          <Text style={[styles.infoValue, { fontWeight: '600' }]}>{settlement.status.toUpperCase()}</Text>
        </View>
        {settlement.approved_date && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Approved On:</Text>
            <Text style={styles.infoValue}>{formatDate(settlement.approved_date)}</Text>
          </View>
        )}
        {settlement.paid_date && (
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Paid On:</Text>
            <Text style={styles.infoValue}>{formatDate(settlement.paid_date)}</Text>
          </View>
        )}
        <View style={styles.infoRow}>
          <Text style={styles.infoLabel}>Created:</Text>
          <Text style={styles.infoValue}>{formatDate(settlement.created_at)}</Text>
        </View>
      </View>

      {/* Data Quality Alert */}
      {uberGapInfo && !uberGapInfo.is_complete && (
        <View style={styles.section}>
          <View style={styles.alertBox}>
            <Ionicons name="alert-circle" size={24} color="#E67E22" />
            <View style={styles.alertContent}>
              <Text style={styles.alertTitle}>Incomplete Data</Text>
              <Text style={styles.alertText}>
                Uber income data is incomplete. Missing {uberGapInfo.gap_days} days may affect accuracy.
              </Text>
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const RentDetailsTab: React.FC<{ settlement: SettlementRecord }> = ({ settlement }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(value);
  };

  return (
    <View style={styles.tabPanel}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Company Share Calculation</Text>
        
        <View style={styles.detailCard}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Daily Rate:</Text>
            <Text style={styles.detailValue}>{formatCurrency(settlement.daily_rate || 0)}/day</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Total Days:</Text>
            <Text style={styles.detailValue}>{settlement.total_days || 0} days</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Base Share:</Text>
            <Text style={styles.detailValue}>{formatCurrency((settlement.daily_rate || 0) * (settlement.total_days || 0))}</Text>
          </View>
          
          {(settlement.rent_discount_days ?? 0) > 0 && (
            <>
              <View style={styles.divider} />
              <View style={styles.detailRow}>
                <Text style={[styles.detailLabel, { color: theme.colors.success }]}>Discount Days:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.success }]}>
                  -{settlement.rent_discount_days} days
                </Text>
              </View>
              <View style={styles.detailRow}>
                <Text style={[styles.detailLabel, { color: theme.colors.success }]}>Discount Amount:</Text>
                <Text style={[styles.detailValue, { color: theme.colors.success }]}>
                  -{formatCurrency(settlement.rent_discount_amount ?? 0)}
                </Text>
              </View>
            </>
          )}
          
          <View style={styles.divider} />
          
          <View style={[styles.detailRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total Company Share:</Text>
            <Text style={[styles.totalValue, { color: theme.colors.error }]}>
              {formatCurrency(settlement.total_rent_charged || 0)}
            </Text>
          </View>
        </View>
      </View>

      {settlement.notes && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Notes</Text>
          <View style={styles.notesBox}>
            <Text style={styles.notesText}>{settlement.notes}</Text>
          </View>
        </View>
      )}
    </View>
  );
};

const IncomeTab: React.FC<{ settlement: SettlementRecord; uberGapInfo: UberGapInfo | null }> = ({ settlement, uberGapInfo }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(value);
  };

  return (
    <View style={styles.tabPanel}>
      {/* Uber Gap Warning */}
      {uberGapInfo && !uberGapInfo.is_complete && (
        <View style={styles.alertBox}>
          <Ionicons name="warning" size={24} color="#E67E22" />
          <View style={styles.alertContent}>
            <Text style={styles.alertTitle}>Uber Data Gap Detected</Text>
            <Text style={styles.alertText}>
              Expected {uberGapInfo.expected_days} days, but only {uberGapInfo.actual_days} days of income recorded.
            </Text>
            <Text style={styles.alertText}>Missing days: {uberGapInfo.gap_dates.join(', ')}</Text>
          </View>
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Income Breakdown</Text>
        
        <View style={styles.detailCard}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Uber Cash:</Text>
            <Text style={styles.detailValue}>{formatCurrency((settlement as any).uber_cash || 0)}</Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Uber Online:</Text>
            <Text style={styles.detailValue}>{formatCurrency((settlement as any).uber_online || 0)}</Text>
          </View>
          
          {(settlement.other_income ?? 0) > 0 && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Other Income:</Text>
              <Text style={styles.detailValue}>{formatCurrency(settlement.other_income ?? 0)}</Text>
            </View>
          )}
          
          <View style={styles.divider} />
          
          <View style={[styles.detailRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total Income:</Text>
            <Text style={[styles.totalValue, { color: theme.colors.success }]}>
              {formatCurrency(settlement.total_income || 0)}
            </Text>
          </View>
        </View>
      </View>

      {/* Income Statistics */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Income Statistics</Text>
        
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Avg Per Day</Text>
            <Text style={styles.statValue}>
              {formatCurrency((settlement.total_income || 0) / (settlement.total_days || 1))}
            </Text>
          </View>
          
          <View style={styles.statCard}>
            <Text style={styles.statLabel}>Income Days</Text>
            <Text style={styles.statValue}>
              {uberGapInfo ? uberGapInfo.actual_days : settlement.total_days || 0}
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const ExpensesTab: React.FC<{ settlement: SettlementRecord }> = ({ settlement }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(value);
  };

  const totalExpenses = (settlement.total_fines || 0) + (settlement.total_salik || 0) + (settlement.total_other_expenses || 0);

  return (
    <View style={styles.tabPanel}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Expense Breakdown</Text>
        
        <View style={styles.detailCard}>
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Fines:</Text>
            <Text style={[styles.detailValue, { color: theme.colors.error }]}>
              {formatCurrency(settlement.total_fines || 0)}
            </Text>
          </View>
          
          <View style={styles.detailRow}>
            <Text style={styles.detailLabel}>Salik Tolls:</Text>
            <Text style={[styles.detailValue, { color: theme.colors.error }]}>
              {formatCurrency(settlement.total_salik || 0)}
            </Text>
          </View>
          
          {(settlement.total_other_expenses ?? 0) > 0 && (
            <View style={styles.detailRow}>
              <Text style={styles.detailLabel}>Other Expenses:</Text>
              <Text style={[styles.detailValue, { color: theme.colors.error }]}>
                {formatCurrency(settlement.total_other_expenses ?? 0)}
              </Text>
            </View>
          )}
          
          <View style={styles.divider} />
          
          <View style={[styles.detailRow, styles.totalRow]}>
            <Text style={styles.totalLabel}>Total Expenses:</Text>
            <Text style={[styles.totalValue, { color: theme.colors.error }]}>
              {formatCurrency(totalExpenses)}
            </Text>
          </View>
        </View>
      </View>

      {totalExpenses === 0 && (
        <View style={styles.emptyState}>
          <Ionicons name="checkmark-circle-outline" size={48} color={theme.colors.success} />
          <Text style={styles.emptyStateText}>No expenses recorded</Text>
        </View>
      )}
    </View>
  );
};

const DiscountsTab: React.FC<{ discounts: RentDiscount[]; settlement: SettlementRecord }> = ({ discounts, settlement }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getDiscountTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      service_day: 'Service Day',
      breakdown: 'Breakdown',
      accident: 'Accident',
      manual: 'Manual Adjustment',
      other: 'Other',
    };
    return labels[type] || type;
  };

  if (discounts.length === 0) {
    return (
      <View style={styles.tabPanel}>
        <View style={styles.emptyState}>
          <Ionicons name="pricetag-outline" size={48} color={theme.colors.textSecondary} />
          <Text style={styles.emptyStateText}>No discounts applied</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabPanel}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Discount Summary</Text>
        <View style={styles.summaryCard}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Discount Days:</Text>
            <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
              {settlement.rent_discount_days ?? 0} days
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Discount Amount:</Text>
            <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
              {formatCurrency(settlement.rent_discount_amount ?? 0)}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Applied Discounts ({discounts.length})</Text>
        {discounts.map((discount) => (
          <View key={discount.id} style={styles.discountCard}>
            <View style={styles.discountHeader}>
              <View style={styles.discountTypeBox}>
                <Text style={styles.discountType}>{getDiscountTypeLabel(discount.discount_type)}</Text>
              </View>
              <Text style={styles.discountAmount}>{formatCurrency(discount.total_discount_amount)}</Text>
            </View>
            
            <View style={styles.discountDetails}>
              <View style={styles.discountRow}>
                <Ionicons name="calendar-outline" size={16} color={theme.colors.textSecondary} />
                <Text style={styles.discountText}>
                  {formatDate(discount.discount_date)} • {discount.discount_days} {discount.discount_days === 1 ? 'day' : 'days'}
                </Text>
              </View>
              
              {discount.reason && (
                <View style={styles.discountRow}>
                  <Ionicons name="information-circle-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.discountText}>{discount.reason}</Text>
                </View>
              )}
              
              {discount.notes && (
                <View style={styles.discountRow}>
                  <Ionicons name="document-text-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.discountText}>{discount.notes}</Text>
                </View>
              )}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const PaymentsTab: React.FC<{ payments: SettlementPayment[]; settlement: SettlementRecord }> = ({ payments, settlement }) => {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPaymentMethodLabel = (method: string) => {
    const labels: Record<string, string> = {
      cash: 'Cash',
      bank_transfer: 'Bank Transfer',
      check: 'Check',
      card: 'Card',
      online_transfer: 'Online Transfer',
      offset: 'Offset',
      other: 'Other',
    };
    return labels[method] || method;
  };

  const totalPaid = payments.reduce((sum, p) => sum + p.payment_amount, 0);

  if (payments.length === 0) {
    return (
      <View style={styles.tabPanel}>
        <View style={styles.emptyState}>
          <Ionicons name="wallet-outline" size={48} color={theme.colors.textSecondary} />
          <Text style={styles.emptyStateText}>No payments recorded</Text>
          <Text style={styles.emptyStateSubtext}>
            Outstanding: {formatCurrency(settlement.outstanding_balance ?? 0)}
          </Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.tabPanel}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Payment Summary</Text>
        <View style={styles.summaryCard}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total Paid:</Text>
            <Text style={[styles.summaryValue, { color: theme.colors.success }]}>
              {formatCurrency(totalPaid)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Outstanding:</Text>
            <Text style={[styles.summaryValue, { color: (settlement.outstanding_balance ?? 0) > 0 ? theme.colors.error : theme.colors.success }]}>
              {formatCurrency(settlement.outstanding_balance ?? 0)}
            </Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Payment Count:</Text>
            <Text style={styles.summaryValue}>{payments.length} {payments.length === 1 ? 'payment' : 'payments'}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Payment History ({payments.length})</Text>
        {payments.map((payment, index) => (
          <View key={payment.id} style={styles.paymentCard}>
            <View style={styles.paymentHeader}>
              <View style={styles.paymentNumber}>
                <Text style={styles.paymentNumberText}>#{payments.length - index}</Text>
              </View>
              <Text style={styles.paymentAmount}>{formatCurrency(payment.payment_amount)}</Text>
            </View>
            
            <View style={styles.paymentDetails}>
              <View style={styles.paymentRow}>
                <Ionicons name="calendar-outline" size={16} color={theme.colors.textSecondary} />
                <Text style={styles.paymentText}>{formatDate(payment.payment_date)}</Text>
              </View>
              
              <View style={styles.paymentRow}>
                <Ionicons name="card-outline" size={16} color={theme.colors.textSecondary} />
                <Text style={styles.paymentText}>{getPaymentMethodLabel(payment.payment_method ?? 'other')}</Text>
              </View>
              
              {payment.payment_reference && (
                <View style={styles.paymentRow}>
                  <Ionicons name="document-text-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.paymentText}>Ref: {payment.payment_reference}</Text>
                </View>
              )}
              
              {payment.notes && (
                <View style={styles.paymentRow}>
                  <Ionicons name="chatbox-outline" size={16} color={theme.colors.textSecondary} />
                  <Text style={styles.paymentText}>{payment.notes}</Text>
                </View>
              )}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

const DocumentsTab: React.FC<{ settlement: SettlementRecord }> = ({ settlement }) => {
  return (
    <View style={styles.tabPanel}>
      <View style={styles.emptyState}>
        <Ionicons name="document-text-outline" size={48} color={theme.colors.textSecondary} />
        <Text style={styles.emptyStateText}>Document Management</Text>
        <Text style={styles.emptyStateSubtext}>Coming soon</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: 16,
  },
  backButton: {
    marginTop: 24,
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: theme.colors.primary,
    borderRadius: 8,
  },
  backButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  backIcon: {
    padding: 8,
    marginRight: 8,
  },
  headerInfo: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  statusBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#FEF5E7',
    borderBottomWidth: 1,
    borderBottomColor: '#F39C12',
    gap: 12,
  },
  warningContent: {
    flex: 1,
  },
  warningTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#E67E22',
    marginBottom: 4,
  },
  warningText: {
    fontSize: 12,
    color: '#E67E22',
  },
  balanceCard: {
    backgroundColor: theme.colors.surface,
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 16,
  },
  balanceItem: {
    alignItems: 'center',
  },
  balanceLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  balanceValue: {
    fontSize: 24,
    fontWeight: '700',
  },
  recordPaymentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.primary,
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  recordPaymentText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  tabBar: {
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: theme.colors.primary,
  },
  tabText: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  tabTextActive: {
    color: theme.colors.primary,
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
  },
  tabPanel: {
    padding: 16,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: 12,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  infoLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  infoValue: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textPrimary,
  },
  summaryCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  highlightRow: {
    backgroundColor: theme.colors.background,
    marginHorizontal: -16,
    paddingHorizontal: 16,
  },
  summaryLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  totalRow: {
    paddingTop: 12,
  },
  totalLabel: {
    fontSize: 16,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  totalValue: {
    fontSize: 18,
    fontWeight: '700',
  },
  divider: {
    height: 1,
    backgroundColor: theme.colors.border,
    marginVertical: 8,
  },
  detailCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  detailLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  notesBox: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  notesText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    lineHeight: 20,
  },
  alertBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#FEF5E7',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#F39C12',
    gap: 12,
  },
  alertContent: {
    flex: 1,
  },
  alertTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#E67E22',
    marginBottom: 4,
  },
  alertText: {
    fontSize: 12,
    color: '#E67E22',
    marginBottom: 2,
  },
  statsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  statCard: {
    flex: 1,
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 16,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  statLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 8,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textSecondary,
    marginTop: 16,
  },
  emptyStateSubtext: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  discountCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  discountHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  discountTypeBox: {
    backgroundColor: theme.colors.success + '20',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  discountType: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.success,
  },
  discountAmount: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.colors.success,
  },
  discountDetails: {
    gap: 8,
  },
  discountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  discountText: {
    flex: 1,
    fontSize: 13,
    color: theme.colors.textPrimary,
  },
  paymentCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  paymentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  paymentNumber: {
    backgroundColor: theme.colors.primary + '20',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  paymentNumberText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.primary,
  },
  paymentAmount: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.colors.success,
  },
  paymentDetails: {
    gap: 8,
  },
  paymentRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  paymentText: {
    flex: 1,
    fontSize: 13,
    color: theme.colors.textPrimary,
  },
  errorBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.error,
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  errorText: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: 14,
  },
});

export default SettlementDetailScreen;
